import { HydrationPosition, HydrationSelectedAccounts } from '../hydration.models';
import type {
  CooAccountDetails,
  CooDestinationAccount,
  CooPosition,
  CooSourceAccount,
} from '../ku4-draft.models';

const FUND_REVERSE: Record<string, string> = {
  VANGUARD_FEDERAL_MONEY_MARKET_FUND: 'FED',
  VANGUARD_CASH_DEPOSIT: 'VCD',
  VANGUARD_CASH_PLUS: 'VCP',
};

const OBJECTIVE_REVERSE: Record<string, 'CAP' | 'Income' | 'Growth' | 'Speculation'> = {
  CAPITAL_PRESERVATION: 'CAP',
  INCOME: 'Income',
  GROWTH: 'Growth',
  SPECULATION: 'Speculation',
};

export interface HydrateAccountDetailsResult {
  selectedAccs?: HydrationSelectedAccounts;
  positions?: HydrationPosition[];
  settlementFund?: string;
  investmentObjectives?: {
    CAP?: boolean;
    Income?: boolean;
    Growth?: boolean;
    Speculation?: boolean;
  };
}

// Normalised here rather than at every call site: minor drafts carry one entry per source
// account, inheritance exactly one, and older minor drafts a single object — all still live.
function normaliseEntries(
  accountDetails: CooAccountDetails[] | CooAccountDetails | undefined,
): CooAccountDetails[] {
  if (!accountDetails) {
    return [];
  }

  return Array.isArray(accountDetails) ? accountDetails : [accountDetails];
}

export function hydrateAccountDetails(
  accountDetails?: CooAccountDetails[] | CooAccountDetails,
): HydrateAccountDetailsResult {
  const entries = normaliseEntries(accountDetails);
  const details = entries[0];

  if (!details) {
    return {};
  }

  const result: HydrateAccountDetailsResult = {};

  // Destination and positions stay anchored to the first entry: the destination
  // is identical on every entry, and only the first source carries positions.
  const destination = details.destinationAccounts?.[0];

  const selectedAccs = buildSelectedAccs(entries, destination);

  if (selectedAccs) {
    result.selectedAccs = selectedAccs;
  }

  const positions = buildPositions(details.sourceAccount, destination);

  if (positions) {
    result.positions = positions;
  }

  if (destination?.settlementFund && FUND_REVERSE[destination.settlementFund]) {
    result.settlementFund = FUND_REVERSE[destination.settlementFund];
  }

  const investmentObjectives = buildInvestmentObjectives(destination);

  if (investmentObjectives) {
    result.investmentObjectives = investmentObjectives;
  }

  return result;
}

function buildSelectedAccs(
  entries: CooAccountDetails[],
  destination: CooDestinationAccount | undefined,
): HydrationSelectedAccounts | undefined {
  const transferFrom = buildTransferFrom(entries);
  const transferTo = buildTransferTo(destination);

  if (!transferFrom && !transferTo) {
    return undefined;
  }

  const selectedAccs: HydrationSelectedAccounts = {};

  if (transferFrom) {
    selectedAccs.transferFrom = transferFrom;
  }

  if (transferTo) {
    selectedAccs.transferTo = transferTo;
  }

  return selectedAccs;
}

function buildSourceAccountData(
  source: CooSourceAccount | undefined,
): { accountId?: string; accountType?: string } | undefined {
  if (!source) {
    return undefined;
  }

  const accountData: { accountId?: string; accountType?: string } = {};

  if (source.accountId) {
    accountData.accountId = source.accountId;
  }

  if (source.accountType) {
    accountData.accountType = source.accountType;
  }

  return Object.keys(accountData).length ? accountData : undefined;
}

// One selected account per entry, so a minor draft with N source accounts
// restores all N. Inheritance is unaffected: its write path emits a single entry.
function buildTransferFrom(
  entries: CooAccountDetails[],
): HydrationSelectedAccounts['transferFrom'] | undefined {
  const selectedAccounts = entries
    .map((entry) => buildSourceAccountData(entry.sourceAccount))
    .filter(
      (accountData): accountData is { accountId?: string; accountType?: string } => !!accountData,
    )
    .map((accountData) => ({ accountData }));

  return selectedAccounts.length ? { selectedAccounts } : undefined;
}

function buildTransferTo(
  destination: CooDestinationAccount | undefined,
): HydrationSelectedAccounts['transferTo'] | undefined {
  if (!destination) {
    return undefined;
  }

  const accountData: { accountId?: string; accountName?: string; accountType?: string } = {};

  if (destination.accountId) {
    accountData.accountId = destination.accountId;
  }

  if (destination.accountName) {
    accountData.accountName = destination.accountName;
  }

  if (destination.accountType) {
    accountData.accountType = destination.accountType;
  }

  const hasAccountData = Object.keys(accountData).length > 0;
  const hasAccountNumber = Boolean(destination.accountNumber);

  if (!hasAccountData && !hasAccountNumber) {
    return undefined;
  }

  const selectedAccount: NonNullable<HydrationSelectedAccounts['transferTo']>['selectedAccount'] =
    {};

  if (hasAccountData) {
    selectedAccount.accountData = accountData;
  }

  if (hasAccountNumber) {
    selectedAccount.accountNumber = destination.accountNumber;
  }

  return { selectedAccount };
}

function buildPositions(
  source: CooSourceAccount | undefined,
  destination: CooDestinationAccount | undefined,
): HydrationPosition[] | undefined {
  const rawPositions = source?.positions?.length ? source.positions : destination?.positions;

  if (!rawPositions?.length) {
    return undefined;
  }

  return rawPositions.map(mapPosition);
}

function mapPosition(position: CooPosition): HydrationPosition {
  const result: HydrationPosition = {};

  if (position.positionId !== undefined) {
    result.positionId = position.positionId;
  }

  if (position.costBasisMethod !== undefined) {
    result.selectedCostBasisMethod = position.costBasisMethod;
  }

  if (position.amount !== undefined) {
    result.selectedAmount = position.amount;
  }

  if (position.giftingAllocationType !== undefined) {
    result.giftingMethod = position.giftingAllocationType;
  }

  if (position.taxLots?.length) {
    result.taxLots = position.taxLots.map((lot) => ({
      lotID: lot.lotId ?? '',
      quantity: lot.amount ?? 0,
    }));
  }

  if (position.additionalTaxLotId !== undefined) {
    result.additionalTaxLot = { lotID: position.additionalTaxLotId };
  }

  return result;
}

function buildInvestmentObjectives(
  destination: CooDestinationAccount | undefined,
): HydrateAccountDetailsResult['investmentObjectives'] | undefined {
  if (!destination?.investmentObjectives?.length) {
    return undefined;
  }

  const objectives: HydrateAccountDetailsResult['investmentObjectives'] = {};

  for (const value of destination.investmentObjectives) {
    const key = OBJECTIVE_REVERSE[value];

    if (key) {
      objectives[key] = true;
    }
  }

  return Object.keys(objectives).length ? objectives : undefined;
}

export function sourceAccountIds(
  accountDetails?: CooAccountDetails[] | CooAccountDetails,
): string[] {
  return normaliseEntries(accountDetails)
    .map((entry) => entry.sourceAccount?.accountId)
    .filter((accountId): accountId is string => !!accountId);
}
